import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '@app/redux/types';
import { CampaignBuilderEnum } from '@app/constants/modules/campaign';
import {
	checkWorkspaceHasAdvanceVideoEntitlement,
	checkWorkspaceHasEmbeddedReelsPlan,
	checkWorkspaceHasReelsPlan,
	checkWorkspaceHasVideoMessagingPlan,
	getAllowedCampaignBuilders,
} from '@app/utils/modules/workspace';

export type UseAllowedCampaignBuilders = {
	allowedBuilders: CampaignBuilderEnum[];
	hasLite: boolean;
	hasLegacy: boolean;
	/** Lite entitled and NOT legacy — Create Campaign goes straight to lite. */
	isLiteOnly: boolean;
	/** Entitled to both — the legacy modal shows an added lite option. */
	isDual: boolean;
	/** Workspace payload (which carries `features`) has loaded. */
	isReady: boolean;
};

export function useAllowedCampaignBuilders(): UseAllowedCampaignBuilders {
	const workspace = useSelector(
		({ workspace }: RootState) => workspace.workspaceInfo,
	);

	const hasWorkspaceVideoPlan = checkWorkspaceHasVideoMessagingPlan(workspace);
	const hasAdvanceVideoEntitlement =
		checkWorkspaceHasAdvanceVideoEntitlement(workspace);

	const hasAReelsPlan =
		checkWorkspaceHasReelsPlan(workspace) ||
		checkWorkspaceHasEmbeddedReelsPlan(workspace);

	return useMemo(() => {
		const allowedBuilders = getAllowedCampaignBuilders({
			hasWorkspaceVideoPlan,
			hasAdvanceVideoEntitlement,
			hasAReelsPlan,
		});

		const hasLite = allowedBuilders.includes(CampaignBuilderEnum.LITE);
		const hasLegacy = allowedBuilders.includes(CampaignBuilderEnum.LEGACY);

		return {
			allowedBuilders,
			hasLite,
			hasLegacy,
			isLiteOnly: hasLite && !hasLegacy,
			isDual: hasLite && hasLegacy,
			isReady: !!workspace?._id,
		};
	}, [
		workspace?._id,
		hasWorkspaceVideoPlan,
		hasAdvanceVideoEntitlement,
		hasAReelsPlan,
	]);
}

export default useAllowedCampaignBuilders;
